import { parseAsJson } from 'nuqs';

export type ListSortDirection = 'ascending' | 'descending';
export interface ListSortRule {
    direction: ListSortDirection;
    field: string;
    fieldLabel: string;
}
export type ListSortQueryValue = ListSortRule[] | null;

export const normalizeListSort = (value: unknown): ListSortQueryValue => {
    const candidates = Array.isArray(value) ? value : value ? [value] : [];
    if (candidates.length > 20) {
        return null;
    }
    const rules: ListSortRule[] = [];
    for (const candidate of candidates) {
        if (!candidate || typeof candidate !== 'object') {
            return null;
        }
        const rule = candidate as Record<string, unknown>;
        if (
            typeof rule.field !== 'string' ||
            !rule.field.trim() ||
            rule.field.length > 250 ||
            (rule.direction !== undefined && rule.direction !== 'ascending' && rule.direction !== 'descending')
        ) {
            return null;
        }
        const field = rule.field.trim();
        if (rules.some((existing) => existing.field === field)) {
            return null;
        }
        rules.push({
            field,
            fieldLabel: typeof rule.fieldLabel === 'string' ? rule.fieldLabel : field,
            direction: rule.direction === 'descending' ? 'descending' : 'ascending',
        });
    }
    return rules.length ? rules : null;
};

export const listSortQuery = parseAsJson<ListSortQueryValue>(normalizeListSort);
export const serializeListSort = (rules: ListSortQueryValue) =>
    rules?.length ? JSON.stringify(rules.map(({ field, direction }) => ({ field, direction }))) : undefined;

export interface SortRule {
    direction?: 'ascending' | 'descending';
    field: string;
}

const comparable = (value: unknown): string | number | null => {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    if (value instanceof Date) {
        return Number.isFinite(value.getTime()) ? value.getTime() : null;
    }
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : null;
    }
    if (typeof value === 'boolean') {
        return value ? 1 : 0;
    }
    if (typeof value !== 'string') {
        return JSON.stringify(value)?.toLowerCase() ?? null;
    }
    const trimmed = value.trim();
    if (!trimmed || trimmed === '-') {
        return null;
    }
    const number = Number(trimmed.replace(/[$,%\s,]/g, ''));
    if (Number.isFinite(number) && /\d/.test(trimmed)) {
        return number;
    }
    const timestamp = Date.parse(trimmed);
    if (Number.isFinite(timestamp) && /\d{4}|\d{1,2}[/-]\d{1,2}/.test(trimmed)) {
        return timestamp;
    }
    return trimmed.toLowerCase();
};

/** Stable, lexicographic priority; empty cells sort last in either direction. */
export const sortListRows = <T extends Record<string, unknown>>(rows: T[], rules: SortRule[]): T[] => {
    if (!rules.length) {
        return rows;
    }
    return [...rows].sort((a, b) => {
        for (const rule of rules) {
            const left = comparable(a[rule.field]);
            const right = comparable(b[rule.field]);
            if (left === null && right === null) {
                continue;
            }
            if (left === null) {
                return 1;
            }
            if (right === null) {
                return -1;
            }
            const order =
                typeof left === 'number' && typeof right === 'number'
                    ? left - right
                    : String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' });
            if (order) {
                return order * (rule.direction === 'descending' ? -1 : 1);
            }
        }
        return 0;
    });
};
